import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { InventoryTransaction } from '../../models/inventory/inventory-transaction';
import { EntityKeyByNumber } from '../../models/inventory/keys/entity-key';
import { ApiResponse, EntityKeyByReference, PaginatedApiResponse } from '@card-software/library';
import { getSkipperErrorHeader } from '../../utils/error-header.util';

@Injectable({
  providedIn: 'root',
})
export class InventoryTransactionProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.inventoryApi + '/api/v1/inventory-transaction';

  public insert(body: InventoryTransaction): Observable<ApiResponse<InventoryTransaction>> {
    return this.httpClient.post<ApiResponse<InventoryTransaction>>(this.baseUrl, body);
  }

  public getRelatedInventoryTransactionsByEntityKey(
    entityKey: EntityKeyByReference,
    skipInterceptor = false,
    populateLotItems = false,
  ): Observable<ApiResponse<InventoryTransaction[]>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.post<ApiResponse<InventoryTransaction[]>>(
      this.baseUrl + '/entity-key?populateLotItems=' + populateLotItems,
      entityKey,
      { headers },
    );
  }

  public upsertLotItemKey(
    lotItemKey: EntityKeyByNumber,
    add: boolean,
    inventoryTransactionReference: string,
  ): Observable<ApiResponse<InventoryTransaction>> {
    return this.httpClient.patch<ApiResponse<InventoryTransaction>>(
      `${this.baseUrl}/${inventoryTransactionReference}/lot-items?add=${add}`,
      lotItemKey,
    );
  }

  public update(inventoryTransaction: InventoryTransaction): Observable<ApiResponse<InventoryTransaction>> {
    return this.httpClient.put<ApiResponse<InventoryTransaction>>(this.baseUrl + '/' + inventoryTransaction.id!, inventoryTransaction);
  }

  public moveOutOfDraft(inventoryTransactionReference: string): Observable<ApiResponse<InventoryTransaction>> {
    return this.httpClient.put<ApiResponse<InventoryTransaction>>(`${this.baseUrl}/${inventoryTransactionReference}/out-of-draft`, {});
  }

  public searchByStatus(
    statuses: number[],
    initiator?: { entityDefinitionCode: number; reference: string },
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<PaginatedApiResponse<InventoryTransaction>>> {
    let queryParams = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (sortField) {
      queryParams += `&sortField=${encodeURIComponent(sortField)}&sortOrder=${sortOrder}`;
    }
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<InventoryTransaction>>>(`${this.baseUrl}/search${queryParams}`, {
      statuses,
      initiator,
    });
  }
}
